import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { GleeMark } from '@/components/Logo';
import { useBranding } from '@/lib/branding';

/**
 * The three pages somebody can reach without an account.
 *
 * They share a shell rather than a component library of their own: these are
 * the only pages on the platform a stranger sees, and three of them drifting
 * apart is more obvious here than anywhere else in the product.
 *
 * None of them ever says whether an email address is known. "No account with
 * that address" is a free membership check for anybody holding a list, so the
 * server answers the same either way and these pages show whatever it said.
 */

function Shell({
  title,
  intro,
  children,
  footer,
}: {
  title: string;
  intro: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const branding = useBranding();
  const productName = branding.product_name ?? 'Glee AI';

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={productName}
              className="mb-5 h-10 w-auto max-w-[14rem] object-contain object-left"
            />
          ) : (
            <GleeMark size={40} className="mb-5" />
          )}
          <h1 className="text-2xl font-semibold text-ink-900 dark:text-ink-50">
            {title}
          </h1>
          <p className="mt-1.5 text-sm text-ink-500 dark:text-ink-400">{intro}</p>
        </div>
        {children}
        {footer && (
          <p className="mt-8 text-sm text-ink-500 dark:text-ink-400">{footer}</p>
        )}
      </div>
    </div>
  );
}

function Alert({ tone, children }: { tone: 'good' | 'bad'; children: ReactNode }) {
  return (
    <div
      role="alert"
      className={
        'animate-fade-in rounded-lg border px-3 py-2.5 text-sm ' +
        (tone === 'good'
          ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/50 dark:text-green-300'
          : 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300')
      }
    >
      {children}
    </div>
  );
}

function message(err: unknown): string {
  return String((err as { message?: string })?.message || err);
}

/* -------------------------------------------------------------------------
 * "I've lost my password"
 * ---------------------------------------------------------------------- */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ message: string }>('/auth/forgot-password', {
        email,
      });
      // Whatever the server said, which is the same sentence whether or not
      // the address is known. Nothing here may improve on it.
      setSent(result.message);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Reset your password"
      intro="We'll email you a link to set a new one."
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            Sign in
          </Link>
        </>
      }
    >
      {sent ? (
        <Alert tone="good">{sent}</Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && <Alert tone="bad">{error}</Alert>}
          <Field label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              placeholder="you@company.com"
              autoFocus
              required
            />
          </Field>
          <Button type="submit" loading={busy} className="mt-1 w-full">
            {busy ? 'Sending…' : 'Email me a link'}
          </Button>
        </form>
      )}
    </Shell>
  );
}

/* -------------------------------------------------------------------------
 * the link from that email
 * ---------------------------------------------------------------------- */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (tooShort || mismatch) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ message: string }>('/auth/reset-password', {
        token,
        password,
      });
      setDone(result.message);
      // Long enough to read the confirmation, short enough not to feel stuck.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Shell
        title="That link is incomplete"
        intro="The reset link is missing its token — it may have been cut short by your email client."
        footer={
          <Link to="/forgot-password" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            Request a new link
          </Link>
        }
      >
        <span />
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password" intro="At least 12 characters.">
      {done ? (
        <Alert tone="good">{done} Taking you to sign in…</Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && <Alert tone="bad">{error}</Alert>}
          <Field
            label="New password"
            required
            error={tooShort ? 'At least 12 characters.' : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••••••"
              autoFocus
              required
            />
          </Field>
          <Field
            label="Confirm password"
            required
            error={mismatch ? 'These do not match.' : undefined}
          >
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••••••"
              required
            />
          </Field>
          <Button
            type="submit"
            loading={busy}
            disabled={busy || tooShort || mismatch || !password}
            className="mt-1 w-full"
          >
            {busy ? 'Saving…' : 'Set password'}
          </Button>
          <p className="text-xs leading-relaxed text-ink-400 dark:text-ink-500">
            Setting a new password signs you out everywhere else.
          </p>
        </form>
      )}
    </Shell>
  );
}

/* -------------------------------------------------------------------------
 * asking for an account
 * ---------------------------------------------------------------------- */
export function RequestAccess() {
  const [form, setForm] = useState({
    company: '',
    contact_name: '',
    email: '',
    phone: '',
    notes: '',
  });
  // Two steps: details, then the code. The address is proved before a human
  // is asked to look at anything, so the operator's review queue is only ever
  // requests from addresses that exist.
  const [step, setStep] = useState<'details' | 'code'>('details');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submitDetails = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ message: string }>(
        '/auth/request-access',
        form,
      );
      setNote(result.message);
      setStep('code');
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ message: string }>(
        '/auth/request-access/verify',
        { email: form.email, code },
      );
      setSent(result.message);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      Already have an account?{' '}
      <Link to="/login" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
        Sign in
      </Link>
    </>
  );

  if (sent) {
    return (
      <Shell
        title="Thanks — you're on the list"
        intro="Your email is confirmed."
        footer={footer}
      >
        <Alert tone="good">{sent}</Alert>
        <p className="mt-4 text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          Somebody reviews every request by hand, so we can set your dialer and
          numbers up correctly rather than leaving you to it. We'll email you
          the moment your account is live.
        </p>
      </Shell>
    );
  }

  if (step === 'code') {
    return (
      <Shell
        title="Check your email"
        intro={`We sent a six-digit code to ${form.email}.`}
        footer={footer}
      >
        <form onSubmit={submitCode} className="space-y-4">
          {error && <Alert tone="bad">{error}</Alert>}
          {note && !error && <Alert tone="good">{note}</Alert>}
          <Field label="Verification code" required>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="text-center text-lg tracking-[0.4em]"
              autoFocus
              required
            />
          </Field>
          <Button
            type="submit"
            loading={busy}
            disabled={busy || code.length < 6}
            className="mt-1 w-full"
          >
            {busy ? 'Checking…' : 'Confirm my email'}
          </Button>
          <button
            type="button"
            onClick={() => {
              setStep('details');
              setError('');
              setNote('');
            }}
            className="w-full text-xs text-ink-500 hover:underline dark:text-ink-400"
          >
            Wrong address, or need a new code? Go back.
          </button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell
      title="Request an account"
      intro="Tell us about your business and we'll get you set up."
      footer={footer}
    >
      <form onSubmit={submitDetails} className="space-y-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <Field label="Company" required>
          <Input
            value={form.company}
            onChange={(e) => set({ company: e.target.value })}
            placeholder="Acme Roofing"
            autoFocus
            required
          />
        </Field>
        <Field label="Your name" required>
          <Input
            value={form.contact_name}
            onChange={(e) => set({ contact_name: e.target.value })}
            placeholder="Dana Whitfield"
            required
          />
        </Field>
        <Field label="Work email" required hint="We'll send a code here to confirm it.">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
            autoComplete="email"
            placeholder="you@company.com"
            required
          />
        </Field>
        <Field label="Phone">
          <Input
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            placeholder="+1 555 0100"
          />
        </Field>
        <Field label="Anything we should know?" hint="Optional.">
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="What you dial, roughly how many agents, anything else."
            className="w-full rounded-md border border-line bg-surface p-2 text-sm text-ink-900 outline-none focus:border-brand-400 dark:text-ink-50"
          />
        </Field>
        <Button type="submit" loading={busy} className="mt-1 w-full">
          {busy ? 'Sending…' : 'Continue'}
        </Button>
        {/* Said before they submit, not after. Somebody expecting instant
            access and getting an inbox message instead feels misled. */}
        <p className="text-xs leading-relaxed text-ink-400 dark:text-ink-500">
          We'll confirm your email, then our team sets the account up by hand —
          so your dialer and numbers are right the first time.
        </p>
      </form>
    </Shell>
  );
}
